import { ChatMessage, AIExpert } from '@garbera/shared';

export interface MessageProps {
  message: ChatMessage;
  expert?: AIExpert | null;
  isTyping?: boolean;
  isRegenerating?: boolean;
  onRetry?: () => void;
}

export interface MessageListProps {
  messages: ChatMessage[];
  expert?: AIExpert | null;
  isLoading?: boolean;
  isTyping?: boolean;
  isRegenerating?: boolean;
  regeneratingMessageId?: number;
  onRetry?: (messageId: number) => void;
  onRetryAborted?: (messageId: number) => void;
  messageStatuses?: Record<number, MessageStatus>;
  inputHeight?: number;
}

export interface MessageInputProps {
  onSendMessage: (content: string, files?: File[]) => void;
  onStopMessage?: () => void;
  isLoading?: boolean;
  isAborting?: boolean;
  placeholder?: string;
  maxLength?: number;
  allowFileUpload?: boolean;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  disabled?: boolean;
  onHeightChange?: (height: number) => void;
}

export interface TypingIndicatorProps {
  isVisible: boolean;
  expertName?: string;
}

export interface FileAttachment {
  id: string;
  file: File;
  preview?: string;
  error?: string;
}

export interface SentFileAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  s3Key?: string;
  url?: string;
}

export interface FileAttachmentProps {
  files: FileAttachment[];
  onFilesChange: (files: FileAttachment[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedTypes?: string[];
  disabled?: boolean;
}

export interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export interface CodeBlockProps {
  code: string;
  language: string;
  className?: string;
}

export interface SentFileItemProps {
  file: SentFileAttachment;
  onDownload?: (file: SentFileAttachment) => void;
}

export interface SentFileListProps {
  files: SentFileAttachment[];
  onDownload?: (file: SentFileAttachment) => void;
}

export interface MessageStatus {
  id: number;
  status: 'sending' | 'sent' | 'failed' | 'aborted';
  timestamp: string;
  error?: string;
  originalContent?: string;
}

export interface ChatError {
  type: 'network' | 'validation' | 'server' | 'file_upload';
  message: string;
  code?: string;
  details?: any;
}

export const MESSAGE_LIMITS = {
  MAX_LENGTH: 4000,
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_FILES: 5,
} as const;

export const ACCEPTED_FILE_TYPES = {
  PDF: '.pdf',
  IMAGES: '.jpg,.jpeg,.png,.gif,.webp',
  DOCUMENTS: '.doc,.docx,.txt,.rtf',
  ALL: '.pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.txt,.rtf',
} as const;

export const TYPING_INDICATORS = {
  DELAY_MS: 1000,
  TIMEOUT_MS: 30000,
} as const;

export const MOCK_RESPONSES = {
  greeting: {
    content: 'こんにちは！私はGarbera AIアシスタントです。どのようなことでお手伝いできますでしょうか？',
    delay: 1500,
  },
  legal: {
    content: '法的な問題についてお答えします。ただし、これは一般的な情報提供であり、具体的な法的アドバイスではありません。重要な法的問題については、必ず専門の弁護士にご相談ください。どのような法的事項についてお聞きになりたいですか？',
    delay: 2000,
  },
  general: {
    content: 'ご質問ありがとうございます。詳しく教えていただけますでしょうか？より具体的な情報をお聞かせいただければ、適切なアドバイスを提供できます。',
    delay: 1200,
  },
} as const;